import { Box, Typography, IconButton, Card, useTheme, Stack } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';

import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Select from '@/components/Common/Select';
import FormSchemaParser from 'components/Common/FormSchemaParser';

import {
  useGetIntegration,
  useGetAvailableIntegrations,
  useGetPostConfigurationFields,
  useUpdateIntegration,
  useCreateOauthIntegration
} from 'apis/business/setting/integrations/hook';

import { useGetLocations } from '@/apis/business/setting/location/hook';
import StreamOrderSettings from './StreamOrderSettings';
import IntegrationPostConfigurationFields from './IntegrationPostConfigurationFields';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';
import CredentialsIcon from '@/assets/icons/CredentialsIcon';
import SyncIcon from '@/assets/icons/SyncIcon';
import SyncTab from './SyncTab';
import ConfigureIntegrationFormSkeleton from './ConfigureIntegrationFormSkeleton';
import CharmHealthForm from '../FormPartials/CharmHealthForm';
import ModeToggleField from '../FormPartials/ModeToggleField';
import { AiOutlineWarning } from 'react-icons/ai';
import { CiRedo } from 'react-icons/ci';
import ConfigurationLoaderBadge from '@/components/Common/ConfigurationLoaderBadge';
import { useToast } from '@/hooks/toast';
import { queryClient } from '@/providers/AllProviders';
import { getApiErrorMessage } from '@/apis/business/setting/integrations/api';

function normalizeConfigurationNumericFields(data: any) {
  if (!data?.configuration) return data;
  const c = data.configuration;
  const pt = c.prep_time_minutes;
  const ps = c.payment_setting;
  return {
    ...data,
    configuration: {
      ...c,
      prep_time_minutes: pt === '' || pt === undefined || pt === null ? 0 : Number(pt) || 0,
      payment_setting: ps
        ? {
            ...ps,
            part_value:
              ps.part_value === '' || ps.part_value === undefined || ps.part_value === null
                ? 0
                : Number(ps.part_value) || 0
          }
        : ps
    }
  };
}

export default function ConfigureIntegrationForm() {
  const { integrationId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast, ToastComponent } = useToast();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState(false);
  const [credentialBadgeStatus, setCredentialBadgeStatus] = useState<null | 'pending' | 'success' | 'failed'>(null);
  const [credentialErrorMessage, setCredentialErrorMessage] = useState('');

  const { data: integrationData, isLoading: loadingIntegration } = useGetIntegration(Number(integrationId), {
    include: 'locations'
  });
  const { data: availableIntegrations, isLoading: loadingAvailable } = useGetAvailableIntegrations({ per_page: 9999 });
  const { mutateAsync: handleCreateOauthIntegration, isPending: isOauthRedirecting } = useCreateOauthIntegration();
  const { data: locations } = useGetLocations();
  const { mutateAsync: updateIntegration, isPending } = useUpdateIntegration();

  const methods = useForm({
    values: {
      ...integrationData,
      location_ids: integrationData?.locations?.map((l: any) => l.id)
    }
  });
  const { handleSubmit, control, watch, setValue, getValues, reset } = methods;
  const formData = watch();
  const selectedCode = watch('code');

  const selectedIntegrationData = useMemo(() => {
    return availableIntegrations?.data?.find((item: any) => item.code === selectedCode);
  }, [availableIntegrations, selectedCode]);

  const isOAuthIntegration = selectedIntegrationData?.connection_type === 'oauth';
  const showReauthenticate = isOAuthIntegration && !integrationData?.active;
  const isInactiveIntegration = !integrationData?.active;

  const handleAuthentication = () => {
    handleCreateOauthIntegration({
      code: selectedCode,
      name: formData.name,
      location_ids: formData.location_ids,
      id: integrationId,
      configuration: formData.configuration
    });
  };

  const { data: postConfigurationFields, isLoading: fetchingPostConfigFields } = useGetPostConfigurationFields(
    integrationId,
    selectedIntegrationData?.post_configuration_required && editing && !isInactiveIntegration
  );

  const memorizedPostConfigFields = useMemo(() => {
    return postConfigurationFields || integrationData?.configuration?.post_configurations;
  }, [postConfigurationFields, integrationData]);

  useEffect(() => {
    if (postConfigurationFields) {
      setValue('configuration.post_configurations', postConfigurationFields);
      if (integrationId) {
        queryClient.invalidateQueries({ queryKey: ['integration', Number(integrationId)] });
      }
    }
  }, [postConfigurationFields, setValue, integrationId]);

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    const oauthSuccess = searchParams.get('oauth_success');

    if (!oauthError && !oauthSuccess) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('oauth_error');
    nextParams.delete('oauth_success');
    setSearchParams(nextParams, { replace: true });

    if (oauthError) {
      showToast(oauthError, 'error');
      setCredentialErrorMessage(oauthError);
      setCredentialBadgeStatus('failed');
    } else {
      showToast('Integration reauthenticated successfully', 'success');
      setCredentialErrorMessage('');
      setCredentialBadgeStatus('success');
    }

    if (integrationId) {
      queryClient.invalidateQueries({ queryKey: ['integration', Number(integrationId)] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['post_configs', integrationId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, integrationId]);

  const onSubmit = async (data: any, disableAfterSave = false, showCredentialBadge = false) => {
    if (showCredentialBadge) {
      setCredentialErrorMessage('');
      setCredentialBadgeStatus('pending');
    }

    try {
      const response = await updateIntegration({
        integration: normalizeConfigurationNumericFields(data)
      } as any);

      if (showCredentialBadge) {
        const failed = !response?.active;
        if (failed) {
          setCredentialErrorMessage('Integration authentication failed. Please check your credentials and update.');
        } else {
          setCredentialErrorMessage('');
        }
        setCredentialBadgeStatus(failed ? 'failed' : 'success');
        if (!failed && integrationId) {
          queryClient.invalidateQueries({ queryKey: ['post_configs', integrationId] });
        }
      } else if (integrationId) {
        // Auto-submit (facility/role) — refresh dependent post-config options
        queryClient.invalidateQueries({ queryKey: ['post_configs', integrationId] });
      }
      if (disableAfterSave) setEditing(false);
    } catch (error) {
      if (showCredentialBadge) {
        setCredentialErrorMessage(getApiErrorMessage(error));
        setCredentialBadgeStatus('failed');
      }
    }
  };

  const handleAutoSubmit = async (val: any) => {
    setValue(val.target.name, val.target.value);
    await handleSubmit((data) => onSubmit(data, false))();
  };

  const handleManualSubmit = handleSubmit((data) => onSubmit(data, true, true));

  const isRpower = selectedCode === 'rpower';
  const isJaneApp = selectedCode === 'jane_app';
  if (loadingIntegration || loadingAvailable) return <ConfigureIntegrationFormSkeleton />;

  const Banner = (
    <Card
      sx={{
        borderRadius: '8px',
        p: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        backgroundColor: theme.palette.primary.light,
        border: '1px solid rgba(0,0,0,0.04)',
        boxShadow: 'none'
      }}
    >
      <img
        src={selectedIntegrationData?.small_avatar_url}
        alt={selectedIntegrationData?.name}
        style={{ width: 40, height: 40 }}
      />
      <Box>
        <Typography fontWeight={600}>{selectedIntegrationData?.name}</Typography>
        <Typography variant='body2' color='text.secondary'>
          <ReactMarkdown components={{ p: ({ ...p }) => <span {...p} /> }}>
            {selectedIntegrationData?.description}
          </ReactMarkdown>
        </Typography>
      </Box>
    </Card>
  );

  if (isRpower) {
    return (
      <FormProvider {...methods}>
        <Box component='form' onSubmit={handleManualSubmit}>
          <Box display='flex' alignItems='center' gap={1} mb={2}>
            <IconButton onClick={() => navigate(-1)}>
              <GoBackButtonIcon />
            </IconButton>
            <Typography fontWeight={500} fontSize={20}>
              Configure Rpower
            </Typography>
          </Box>

          {Banner}

          <Box bgcolor='#fff' padding={2} borderRadius='8px'>
            <Box mb={3}>
              <Typography fontWeight={500} fontSize={16}>
                API Credentials
              </Typography>
              <Typography fontWeight={400} fontSize={14} color='text.secondary'>
                Manage the connection credentials for {selectedIntegrationData?.name}.
              </Typography>
            </Box>
            {!integrationData?.active && integrationData?.inactivate_at && (
              <Stack
                direction='row'
                bgcolor='error.light'
                color='error.main'
                py='8px'
                px='16px'
                gap={0.5}
                mb={3}
                borderRadius='8px'
                alignItems='flex-start'
              >
                <Box mt={0.1}>
                  <AiOutlineWarning size={14} />
                </Box>
                <Typography fontSize={14}>
                  Integration connection could not be authenticated. Please verify or update the connection details and
                  save. The system will automatically validate the connection again.
                </Typography>
              </Stack>
            )}
            {selectedIntegrationData?.pre_configurations?.length && (
              <FormSchemaParser
                fields={selectedIntegrationData.pre_configurations}
                control={control}
                namePrefix='configuration'
                gridSize={12}
                spacing='16px'
              />
            )}
          </Box>
          <Box mt={3} bgcolor='#fff' paddingX={2} paddingTop={2} borderRadius='8px'>
            <StreamOrderSettings />
          </Box>

          <Box mt={4} display='flex' justifyContent='flex-end'>
            <Button type='submit' isPending={isPending}>
              Save
            </Button>
          </Box>
        </Box>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box component='form' onSubmit={handleManualSubmit}>
        {/* header */}
        <Box display='flex' alignItems='center' gap={1} mb={2}>
          <IconButton onClick={() => navigate(-1)}>
            <GoBackButtonIcon />
          </IconButton>
          <Typography fontWeight={500} fontSize={20}>
            Configure {selectedIntegrationData?.name}
          </Typography>
        </Box>

        {Banner}

        {/* segmented tabs */}
        <Box
          sx={{
            display: 'flex',
            backgroundColor: '#fff',
            borderRadius: 2,
            p: '6px',
            mb: 3
          }}
        >
          <Box
            onClick={() => setTab(0)}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1.2,
              borderRadius: 2,
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'all .2s ease',
              backgroundColor: tab === 0 ? theme.palette.primary.light : 'transparent',
              color: tab === 0 ? theme.palette.text.primary : theme.palette.text.secondary,
              boxShadow: tab === 0 ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
            }}
          >
            <CredentialsIcon />
            <Typography variant='body2'>Credentials</Typography>
          </Box>

          <Box
            onClick={() => setTab(1)}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1, // space between icon and text
              textAlign: 'center',
              py: 1.2,
              borderRadius: 2,
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'all .2s ease',
              backgroundColor: tab === 1 ? theme.palette.primary.light : 'transparent',
              color: tab === 1 ? theme.palette.text.primary : theme.palette.text.secondary,
              boxShadow: tab === 1 ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
            }}
          >
            <SyncIcon />
            <Typography variant='body2'>Sync</Typography>
          </Box>
        </Box>

        {tab === 0 && (
          <>
            <Box bgcolor='#fff' padding={2} borderRadius={2}>
              {credentialBadgeStatus && (
                <ConfigurationLoaderBadge
                  onClose={() => {
                    setCredentialBadgeStatus(null);
                    setCredentialErrorMessage('');
                  }}
                  status={credentialBadgeStatus}
                  subHeading={credentialBadgeStatus === 'failed' ? credentialErrorMessage || undefined : undefined}
                />
              )}
              <Stack direction='row' justifyContent='space-between' mb={2}>
                <Box>
                  <Typography fontWeight={500} fontSize={16}>
                    API Credentials
                  </Typography>
                  <Typography fontWeight={400} fontSize={14} color='text.secondary'>
                    Manage the connection credentials for {selectedIntegrationData?.name}.
                  </Typography>
                </Box>

                <Stack direction='row' gap={1} alignItems='center'>
                  {showReauthenticate && (
                    <Button
                      startIcon={<CiRedo size={18} />}
                      onClick={handleAuthentication}
                      isPending={isOauthRedirecting}
                      disabled={isOauthRedirecting}
                    >
                      Reauthenticate
                    </Button>
                  )}

                  {!editing && (
                    <Box
                      component='span'
                      bgcolor='primary.main'
                      color='white'
                      height={38}
                      borderRadius={8}
                      py={1}
                      px={2}
                      fontSize={14}
                      fontWeight={500}
                      onClick={() => setEditing(true)}
                      sx={{ cursor: 'pointer' }}
                    >
                      Edit
                    </Box>
                  )}
                </Stack>
              </Stack>
              {!integrationData?.active && integrationData?.inactivate_at && credentialBadgeStatus !== 'failed' && (
                <Stack
                  direction='row'
                  bgcolor='error.light'
                  color='error.main'
                  py='8px'
                  px='16px'
                  gap={0.5}
                  mb={3}
                  borderRadius='8px'
                  alignItems='flex-start'
                >
                  <Box mt={0.1}>
                    <AiOutlineWarning size={14} />
                  </Box>
                  <Typography fontSize={14}>
                    {credentialErrorMessage ||
                      (isOAuthIntegration
                        ? 'Integration connection could not be authenticated. Click Reauthenticate to reconnect your account via OAuth.'
                        : 'Integration connection could not be authenticated. Please verify or update the connection details and save. The system will automatically validate the connection again.')}
                  </Typography>
                </Stack>
              )}
              <Box>
                <Input disabled={!editing} name='name' label='Friendly Name' control={control} fullWidth required />

                <Box mt={2}>
                  <Select
                    name='location_ids'
                    control={control}
                    label='Location'
                    disabled={!editing}
                    options={
                      locations?.data?.map((l: any) => ({
                        label: l.name,
                        value: l.id
                      })) ?? []
                    }
                    required
                  />
                </Box>

                {selectedIntegrationData?.pre_configurations?.length ? (
                  <>
                    {selectedIntegrationData?.connection_type === 'credentials' &&
                      [
                        'boulevard',
                        'meevo',
                        'itrust',
                        'dentrix_ascend',
                        'akitu_one',
                        'athena_health',
                        'nex_health'
                      ].includes(selectedIntegrationData?.code || '') && (
                        <ModeToggleField namePrefix='configuration' control={control} disabled />
                      )}
                    <Box mt={2}>
                      <FormSchemaParser
                        fields={selectedIntegrationData.pre_configurations}
                        disabled={!editing}
                        control={control}
                        namePrefix='configuration'
                        gridSize={12}
                        spacing='16px'
                      />
                    </Box>
                    {selectedIntegrationData?.connection_type === 'custom' &&
                      selectedIntegrationData?.code === 'charm_health' && (
                        <CharmHealthForm
                          namePrefix='configuration'
                          control={control}
                          disabled={!editing}
                          isActive={Boolean(integrationData?.active)}
                        />
                      )}
                  </>
                ) : (
                  ''
                )}

                {selectedIntegrationData?.post_configuration_required &&
                !(selectedCode === 'charm_health' && isInactiveIntegration) ? (
                  <Box mt={2}>
                    <IntegrationPostConfigurationFields
                      selectedCode={selectedCode}
                      integrationId={integrationId}
                      configFields={memorizedPostConfigFields}
                      namePrefix='configuration'
                      control={control}
                      disabled={!editing}
                      isLoading={fetchingPostConfigFields}
                      getValues={getValues}
                      setValue={setValue}
                      submitForm={(val: any) => handleAutoSubmit(val)}
                    />
                  </Box>
                ) : (
                  ''
                )}
              </Box>
            </Box>
            {editing && (
              <Stack direction='row' gap={1} justifyContent='flex-end' mt={2}>
                <Button
                  color='secondary'
                  variant='outlined'
                  fullWidth
                  sx={{ height: '5px', width: { xs: '100%', lg: 'auto' }, minWidth: { lg: 'auto' } }}
                  onClick={() => {
                    reset();
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  loadingText={isJaneApp ? 'Validating Credentials' : ''}
                  isPending={isPending}
                  sx={{ height: '5px' }}
                >
                  Save
                </Button>
              </Stack>
            )}
          </>
        )}

        {tab === 1 && (
          <SyncTab
            control={control}
            setValue={setValue}
            selectedIntegrationData={selectedIntegrationData}
            totalSyncedContacts={integrationData?.total_synced_contacts}
          />
        )}
      </Box>
      {ToastComponent}
    </FormProvider>
  );
}
